import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

/**
 * Hook to sync filter state with URL query parameters
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.filters - Current filter state from useFilters
 * @param {Function} options.setFilters - Function to update filter state
 * @param {Object} options.paramMapping - Map filter keys to URL param names (optional)
 * @param {boolean} options.shallow - Use shallow routing (default: true)
 *
 * @example
 * const { filters, setFilters } = useFilters({ state: [], type: [] });
 *
 * useURLSync({
 *   filters,
 *   setFilters,
 *   paramMapping: { state: 'state', type: 'designation' }
 * });
 */
export function useURLSync({
  filters,
  setFilters,
  paramMapping = {},
  shallow = true,
}) {
  const router = useRouter();
  const isInitialized = useRef(false);
  const isUpdatingURL = useRef(false);
  const isNavigating = useRef(false);
  const urlUpdateTimeout = useRef(null);

  // Listen for route changes to cancel pending URL updates
  useEffect(() => {
    const handleRouteChangeStart = () => {
      isNavigating.current = true;
      // Cancel any pending URL updates
      if (urlUpdateTimeout.current) {
        clearTimeout(urlUpdateTimeout.current);
        urlUpdateTimeout.current = null;
      }
    };

    const handleRouteChangeComplete = () => {
      isNavigating.current = false;
    };

    const handleRouteChangeError = () => {
      isNavigating.current = false;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router.events]);

  // Get URL param name for a filter key
  const getParamName = useCallback(
    (filterKey) => {
      return paramMapping[filterKey] || filterKey;
    },
    [paramMapping]
  );

  // Get filter key from URL param name
  const getFilterKey = useCallback(
    (paramName) => {
      const entry = Object.entries(paramMapping).find(
        ([, value]) => value === paramName
      );
      return entry ? entry[0] : paramName;
    },
    [paramMapping]
  );

  // Initialize filters from URL on mount
  useEffect(() => {
    if (!router.isReady || isInitialized.current) return;

    const urlFilters = {};
    let hasUrlFilters = false;

    // Parse URL params into filter state
    Object.keys(router.query).forEach((paramName) => {
      // Skip non-filter params (like slug, page, etc.)
      if (['slug', 'page'].includes(paramName)) return;

      const filterKey = getFilterKey(paramName);
      const value = router.query[paramName];

      // Handle array values (multiple selections)
      if (Array.isArray(value)) {
        urlFilters[filterKey] = value;
      } else if (value) {
        urlFilters[filterKey] = [value];
      }

      if (urlFilters[filterKey]?.length > 0) {
        hasUrlFilters = true;
      }
    });

    if (hasUrlFilters) {
      setFilters((prev) => ({
        ...prev,
        ...urlFilters,
      }));
    }

    isInitialized.current = true;
  }, [router.isReady, router.query, getFilterKey, setFilters]);

  // Update URL when filters change (with debouncing to prevent rapid updates)
  useEffect(() => {
    if (!router.isReady || !isInitialized.current || isUpdatingURL.current)
      return;

    // Clear any pending URL update
    if (urlUpdateTimeout.current) {
      clearTimeout(urlUpdateTimeout.current);
    }

    // Debounce URL updates by 300ms to prevent rapid changes
    urlUpdateTimeout.current = setTimeout(() => {
      // Don't update URL if navigation is in progress
      if (isNavigating.current) {
        return;
      }

      isUpdatingURL.current = true;

      const newQuery = { ...router.query };

      // Remove existing filter params
      Object.keys(filters).forEach((filterKey) => {
        const paramName = getParamName(filterKey);
        delete newQuery[paramName];
      });

      // Add current filter values
      Object.entries(filters).forEach(([filterKey, values]) => {
        if (values && values.length > 0) {
          const paramName = getParamName(filterKey);
          // Use single value if only one, array if multiple
          newQuery[paramName] = values.length === 1 ? values[0] : values;
        }
      });

      // Preserve page param only if filters haven't changed
      // (reset to page 1 when filters change)
      const currentFilterStr = JSON.stringify(
        Object.fromEntries(
          Object.entries(router.query).filter(
            ([k]) => !['slug', 'page'].includes(k)
          )
        )
      );
      const newFilterStr = JSON.stringify(
        Object.fromEntries(
          Object.entries(newQuery).filter(
            ([k]) => !['slug', 'page'].includes(k)
          )
        )
      );

      if (currentFilterStr !== newFilterStr) {
        delete newQuery.page; // Reset pagination when filters change
      }

      router.replace(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow }
      );

      // Reset flag after a short delay to allow router to update
      setTimeout(() => {
        isUpdatingURL.current = false;
      }, 100);
    }, 300);

    // Cleanup timeout on unmount or when filters change again
    return () => {
      if (urlUpdateTimeout.current) {
        clearTimeout(urlUpdateTimeout.current);
      }
    };
  }, [filters, router, getParamName, shallow]);

  /**
   * Get a shareable URL with current filters
   */
  const getShareableURL = useCallback(() => {
    if (typeof window === 'undefined') return '';

    const url = new URL(window.location.href);

    // Clear existing filter params
    Object.keys(filters).forEach((filterKey) => {
      const paramName = getParamName(filterKey);
      url.searchParams.delete(paramName);
    });

    // Add current filters
    Object.entries(filters).forEach(([filterKey, values]) => {
      if (values && values.length > 0) {
        const paramName = getParamName(filterKey);
        values.forEach((value) => {
          url.searchParams.append(paramName, value);
        });
      }
    });

    return url.toString();
  }, [filters, getParamName]);

  return {
    getShareableURL,
    isReady: router.isReady && isInitialized.current,
  };
}

export default useURLSync;
